<template>
  <!-- Loading State -->
    <div v-if="isFile1Loading || isFile2Loading" class="mt-6 flex items-center justify-center" style="height: 500px;">
        <div class="text-gray-500 flex flex-col items-center">
            <svg class="animate-spin h-10 w-10 text-primary mb-4" xmlns="http://www.w3.org/2000/svg" fill="none"
                 viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                </path>
            </svg>
            <p>Loading spreadsheet data...</p>
        </div>
    </div>

  <!-- Error State -->
    <div v-else-if="file1Error" class="mt-6 flex items-center justify-center" style="height: 500px;">
        <div class="text-red-500 flex flex-col items-center">
            <svg class="h-10 w-10 text-red-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                 xmlns="http://www.w3.org/2000/svg">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <p>{{ file1Error }}</p>
            <button @click="retryFile1Load"
                    class="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500">
                Retry Loading
            </button>
        </div>
    </div>

  <!-- AI Template Not Found Warning - Show this when file2Error exists but file1 is loaded successfully -->
    <div v-else-if="file2Error && !file1Error && !isFile1Loading" class="relative">
        <!-- Show warning message at the top -->
        <div class="mb-4 p-3 bg-yellow-100 border border-yellow-300 rounded-md text-yellow-700 flex items-center">
            <svg class="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                 xmlns="http://www.w3.org/2000/svg">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
            </svg>
            <span>{{ file2Error }}</span>
        </div>

        <!-- Continue showing the spreadsheet with just the uploaded template -->
        <div class="grid grid-cols-3 gap-6">
            <!-- Spreadsheet Viewers -->
            <div class="col-span-2 border border-gray-200 rounded-lg p-4">
                <h4 class="text-base font-medium text-gray-700 mb-3">Spreadsheet</h4>
                <!-- Action Buttons -->
                <div class="mt-4 flex items-center p-1">
                    <!-- All buttons in a single flex container -->
                    <div class="flex space-x-2">
                        <button @click="exportToExcel"
                                class="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                            <svg class="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                                 xmlns="http://www.w3.org/2000/svg">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path>
                            </svg>
                            Export Excel
                        </button>
                        <button @click="exportToCsv"
                                class="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                            <svg class="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                                 xmlns="http://www.w3.org/2000/svg">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z">
                                </path>
                            </svg>
                            Export CSV
                        </button>
                    </div>
                </div>
                <div class="h-[60vh]">
                    <ExcelViewer ref="excelViewerComponent1" :fileData="excelFile1Data || undefined" :readOnly="true"
                                 :cellCoordinates="cellDataArray.map(item => item.cellCoordinate)"
                                 :select="selectedCoordinates"
                                 @cell-values="handleCellValues1"/>
                </div>
            </div>
            <div class="col-span-1">
                <!-- Comparison Table - Show a message that comparison is not available -->
                <div class="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
                    <h4 class="text-base font-medium text-gray-700 mb-3">Comparison Results</h4>
                    <div class="flex items-center justify-center h-[60vh]">
                        <div class="text-gray-500 text-center">
                            <svg class="h-12 w-12 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor"
                                 viewBox="0 0 24 24"
                                 xmlns="http://www.w3.org/2000/svg">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                      d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                            </svg>
                            <p class="text-lg font-medium mb-2">No Comparison Available</p>
                            <p class="text-sm">No AI-generated template was found for comparison. You can still view and
                                edit the uploaded template.</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Save Success Message -->
        <div v-if="saveSuccess"
             class="mt-4 p-3 bg-green-100 border border-green-300 rounded-md text-green-700 flex items-center">
            <svg class="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                 xmlns="http://www.w3.org/2000/svg">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
            </svg>
            <span>{{ saveSuccess }}</span>
        </div>

        <!-- Save Error Message -->
        <div v-if="saveError"
             class="mt-4 p-3 bg-red-100 border border-red-300 rounded-md text-red-700 flex items-center">
            <svg class="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                 xmlns="http://www.w3.org/2000/svg">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <span>{{ saveError }}</span>
        </div>
    </div>

  <!-- Saving Indicator -->
    <div v-else-if="isSaving"
         class="mt-6 flex items-center justify-center absolute inset-0 bg-white bg-opacity-75 z-10">
        <div class="text-gray-800 flex flex-col items-center">
            <svg class="animate-spin h-10 w-10 text-primary mb-4" xmlns="http://www.w3.org/2000/svg" fill="none"
                 viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                </path>
            </svg>
            <p>Saving changes...</p>
        </div>
    </div>

  <!-- Spreadsheet Container -->
    <div v-else class="relative">

        <div class="grid grid-cols-3 gap-6">
            <!-- Spreadsheet Viewers -->
            <div class="col-span-2 border border-gray-200 rounded-lg p-4">
                <h4 class="text-base font-medium text-gray-700 mb-3">Spreadsheet</h4>
                <!-- Action Buttons -->
                <div class="mt-4 flex justify-between items-center p-1">
                    <!-- All buttons in a single flex container -->
                    <div class="flex space-x-2">
                        <button @click="exportToExcel"
                                class="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                            <svg class="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                                 xmlns="http://www.w3.org/2000/svg">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path>
                            </svg>
                            Export Excel
                        </button>
                        <button @click="exportToCsv"
                                class="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                            <svg class="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                                 xmlns="http://www.w3.org/2000/svg">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z">
                                </path>
                            </svg>
                            Export CSV
                        </button>
                        <!-- Switch Button (only shown in switch layout) -->
                        <button v-if="currentLayout === 'full'" @click="toggleActiveViewer"
                                class="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                            <svg class="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                                 xmlns="http://www.w3.org/2000/svg">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                      d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"></path>
                            </svg>
                            Switch to {{ activeViewer === 1 ? 'AI Generated' : 'Actual' }}
                        </button>

                        <!-- Layout Toggle Button -->
                        <button @click="toggleLayout"
                                class="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                            <svg class="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                                 xmlns="http://www.w3.org/2000/svg">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                      d="M4 6h16M4 12h16m-7 6h7"></path>
                            </svg>
                            {{
                            currentLayout === 'side-by-side' ? 'Change Layout to Full View' : 'Change Layout to Side by Side'
                            }}
                        </button>
                    </div>
                </div>
                <div class="h-[60vh]" :class="[
          'flex gap-4',
          currentLayout === 'side-by-side' ? 'grid grid-cols-2' : ''
        ]">
                    <div :class="[
            currentLayout === 'side-by-side' ? 'flex-1' : 'w-full',
            currentLayout === 'full' && activeViewer !== 1 ? 'hidden' : ''
          ]">
                        <ExcelViewer ref="excelViewerComponent1" :fileData="excelFile1Data || undefined"
                                     :readOnly="true"
                                     :cellCoordinates="cellDataArray.map(item => item.cellCoordinate)"
                                     :select="selectedCoordinates"
                                     @cell-values="handleCellValues1"/>
                    </div>
                    <div :class="[
            currentLayout === 'side-by-side' ? 'flex-1' : 'w-full',
            currentLayout === 'full' && activeViewer !== 2 ? 'hidden' : ''
          ]">
                        <ExcelViewer ref="excelViewerComponent2" :fileData="excelFile2Data || undefined"
                                     :readOnly="true"
                                     :cellCoordinates="cellDataArray.map(item => item.cellCoordinate)"
                                     :select="selectedCoordinates"
                                     @cell-values="handleCellValues2"/>
                    </div>
                </div>
            </div>
            <div class="col-span-1">

                <!-- Comparison Table -->
                <div class="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
                    <h4 class="text-base font-medium text-gray-700 mb-3">Comparison Results</h4>
                    <div class="overflow-auto h-[60vh]">
                        <table class="w-full text-sm text-left text-gray-500 dark:text-gray-400">
                            <thead
                                    class="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400 sticky top-0">
                            <tr>
                                <th class="py-3 px-6">Field Name</th>
                                <th class="py-3 px-6">Cell Coordinate</th>
                                <th class="py-3 px-6">Relative Deviation ↓</th>
                                <th class="py-3 px-6">Manual</th>
                                <th class="py-3 px-6">AI Forecast</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="item in combinedValues" :key="item.fieldName"
                                class="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-100 hover:shadow-md hover:translate-y-[-1px] cursor-pointer transition-all duration-150"
                                :class="{ 'bg-blue-200 shadow-md translate-y-[-1px]': selectedRow === item.cellCoordinate }"
                                @click="handleRowClick(item.cellCoordinate)">
                                <td class="py-4 px-6 hover:translate-x-1 transition-transform duration-150">
                                    {{ item.fieldName }}
                                </td>
                                <td class="py-4 px-6">{{ item.cellCoordinate }}</td>
                                <td class="py-4 px-6" :class="{
                    'text-green-500': item.deviationStatus === 'OK',
                    'text-orange-500': item.deviationStatus === 'WARN',
                    'text-red-500': item.deviationStatus === 'ERROR'
                  }">{{ item.relativeDeviation }}
                                </td>
                                <td class="py-4 px-6">{{ item.manualValue }}</td>
                                <td class="py-4 px-6">{{ item.aiForecastValue }}</td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>

        <!-- Save Success Message -->
        <div v-if="saveSuccess"
             class="mt-4 p-3 bg-green-100 border border-green-300 rounded-md text-green-700 flex items-center">
            <svg class="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                 xmlns="http://www.w3.org/2000/svg">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
            </svg>
            <span>{{ saveSuccess }}</span>
        </div>

        <!-- Save Error Message -->
        <div v-if="saveError"
             class="mt-4 p-3 bg-red-100 border border-red-300 rounded-md text-red-700 flex items-center">
            <svg class="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                 xmlns="http://www.w3.org/2000/svg">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <span>{{ saveError }}</span>
        </div>
    </div>
</template>

<script lang="ts">
import {defineComponent, ref, onMounted, watch, computed} from 'vue';
import ExcelViewer from "@/components/ExcelViewer.vue";
import {logger} from '@/services/logger';
import {DOCUMENT_STATUS, REPORT_TYPE} from "@/types/document";
import {supabase} from '@/services/supabase';
import {useSpreadsheetChanges} from '@/composables/useSpreadsheetChanges';

interface ExcelViewerInstance {
    getHotInstance: () => any;
    getWorkbook: () => any;
    exportToExcel: (filename: string) => void;
    exportToCsv: (filename: string) => void;
    getCurrentData: () => { data: any[][]; currentSheet: string } | null;
}

type LayoutType = 'side-by-side' | 'full';
type ActiveViewerType = 1 | 2;

interface CellData {
    fieldName: string;
    cellCoordinate: string;
    manualValue?: string;
    aiForecastValue?: string;
    absoluteDeviation?: number;
    relativeDeviation?: string;
    deviationStatus?: 'OK' | 'WARN' | 'ERROR';
}

export default defineComponent({
    name: 'DocumentComparisonSpreadsheet',
    components: {
        ExcelViewer
    },
    props: {
        documentData: {
            type: Object,
            default: () => ({})
        }
    },
    emits: ['save'],
    setup(props, {emit}) {
        // Initialize the spreadsheet changes composable
        const {saveChangesToDb: saveChangesToDbComposable} = useSpreadsheetChanges();
        const excelViewerComponent1 = ref<ExcelViewerInstance | null>(null);
        const excelViewerComponent2 = ref<ExcelViewerInstance | null>(null);
        const isFile1Loading = ref(false);
        const file1Error = ref<string | null>(null);
        const excelFile1Data = ref<ArrayBuffer | null>(null);
        const file1Id = ref<string>('');
        const file1Info = ref<any>(null);

        const isFile2Loading = ref(false);
        const file2Error = ref<string | null>(null);
        const excelFile2Data = ref<ArrayBuffer | null>(null);
        const file2Id = ref<string>('');
        const file2Info = ref<any>(null);
        const isSaving = ref(false);
        const saveSuccess = ref<string | null>(null);
        const saveError = ref<string | null>(null);

        const reportType = ref<REPORT_TYPE>(REPORT_TYPE.COM)
        const cellDataArray = ref<CellData[]>([])

        // Layout state
        const currentLayout = ref<LayoutType>('full');
        const activeViewer = ref<ActiveViewerType>(1);

        // Toggle between layouts
        const toggleLayout = () => {
            currentLayout.value = currentLayout.value === 'full' ? 'side-by-side' : 'full';
            // Reset active viewer when switching layouts
            activeViewer.value = 1;
        };

        // Toggle between viewers in switch layout
        const toggleActiveViewer = () => {
            activeViewer.value = activeViewer.value === 1 ? 2 : 1;
        };

        // Format date for display
        const formatDate = (date: Date): string => {
            return date.toLocaleDateString('de-DE', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            });
        };

        // Format document status for display
        const formatDocumentStatus = (status: DOCUMENT_STATUS): string => {
            switch (status) {
                case DOCUMENT_STATUS.CREATING:
                    return 'Creating';
                case DOCUMENT_STATUS.REVIEW_CREATED:
                    return 'Review Created';
                case DOCUMENT_STATUS.EMAIL_PENDING:
                    return 'Email Pending';
                case DOCUMENT_STATUS.READY_FOR_DOWNLOAD:
                    return 'Ready for Download';
                case DOCUMENT_STATUS.DOWNLOADED:
                    return 'Downloaded';
                case DOCUMENT_STATUS.UPLOADED_CHECKING:
                    return 'Uploaded and Checking';
                case DOCUMENT_STATUS.ADJUSTMENT_NEEDED:
                    return 'Adjustment Needed';
                case DOCUMENT_STATUS.REVISION_NEEDED:
                    return 'Ready for Revision';
                default:
                    return status;
            }
        };

        // Fetch the Excel file from Supabase for a given documentId
        const fetchExcelFile = async (fileId: string, fileDataRef: any, fileErrorRef: any, isFileLoadingRef: any, fileInfoRef: any) => {
            fileErrorRef.value = null;
            fileDataRef.value = null;
            isFileLoadingRef.value = true;
            saveSuccess.value = null;
            saveError.value = null;

            try {
                // Fetch the file record from the files table
                const {data: fileRecord, error: fileError} = await supabase
                    .from('files')
                    .select('*')
                    .eq('id', fileId)
                    .single()

                if (fileError) {
                    throw new Error(`Error fetching file record: ${fileError.message}`);
                }

                if (!fileRecord) {
                    throw new Error(`File record not found for ID: ${fileId}`);
                }

                logger.info('File record fetched', {fileRecord: fileRecord});

                // Check if we have a URL to download the file directly
                if (fileRecord.url) {
                    try {
                        // Attempt to download directly from the URL
                        const response = await fetch(fileRecord.url);

                        if (!response.ok) {
                            throw new Error(`Failed to fetch file: ${response.status} ${response.statusText}`);
                        }

                        const fileData = await response.arrayBuffer();
                        fileDataRef.value = fileData;

                        logger.info('Excel file loaded successfully via direct URL', {
                            fileId: fileId,
                            url: fileRecord.url
                        });
                        return;
                    } catch (fetchError) {
                        logger.warn('Failed to download file via direct URL, falling back to Supabase storage', {
                            error: fetchError,
                            url: fileRecord.url
                        });
                        // Continue to the Supabase storage fallback below
                    }
                }

                // Extract information from the URL to download from Supabase
                // This is a fallback in case direct download fails
                const urlInfo = extractStorageInfoFromUrl(fileRecord.url);

                if (!urlInfo) {
                    throw new Error('Could not determine the storage path from URL');
                }

                logger.info('Extracted storage info', {urlInfo});

                // Download the file from Supabase Storage
                const {data: storageData, error: storageError} = await supabase.storage
                    .from(urlInfo.bucket)
                    .download(urlInfo.path);

                if (storageError) {
                    throw new Error(`Error downloading file from storage: ${storageError.message}`);
                }

                if (!storageData) {
                    throw new Error('No file data returned from storage');
                }

                // Save the file info to the fileInfoRef
                fileInfoRef.value = await storageData.arrayBuffer()

                console.log('fileInfoRef.value', fileInfoRef.value);

                logger.info('Excel file loaded successfully from Supabase storage', {
                    fileId: fileId,
                    bucket: urlInfo.bucket,
                    path: urlInfo.path
                });

            } catch (error) {
                logger.error('Error fetching Excel file', {error});
                fileErrorRef.value = error instanceof Error ? error.message : 'An unknown error occurred';
                fileDataRef.value = null;
            } finally {
                isFileLoadingRef.value = false;
            }
        };

        // Helper function to extract bucket and path from Supabase URL
        const extractStorageInfoFromUrl = (url: string) => {
            if (!url) return null;

            try {
                // Parse the URL to extract bucket and path
                // Example URL: https://extravagant.ai-core.dev.azcld.advisori.de/storage/v1/object/public/Templates/1746703577729-1746616064363-EMPTY_2025_responsible_20planners_20report_COM_template_20(3).xlsx

                const regex = /\/storage\/v1\/object\/(?:public|sign)\/([^/]+)\/(.+)/;
                const matches = url.match(regex);

                if (matches && matches.length >= 3) {
                    return {
                        bucket: matches[1],
                        path: decodeURIComponent(matches[2])
                    };
                }

                return null;
            } catch (error) {
                logger.error('Error parsing storage URL', {url, error});
                return null;
            }
        };


        // Retry loading the file for a given file (1 or 2)
        const retryFile1Load = () => {
            fetchExcelFile(file1Id.value, excelFile1Data, file1Error, isFile1Loading, file1Info);
        };
        const retryFile2Load = () => {
            fetchExcelFile(file2Id.value, excelFile2Data, file2Error, isFile2Loading, file2Info);
        };

        // Export to Excel
        const exportToExcel = () => {
            if (excelViewerComponent1.value) {
                const filename = `${props.documentData.name || 'spreadsheet'}.xlsx`;
                excelViewerComponent1.value.exportToExcel(filename);
            }
        };

        // Export to CSV
        const exportToCsv = () => {
            if (excelViewerComponent1.value) {
                const filename = `${props.documentData.name || 'spreadsheet'}.csv`;
                excelViewerComponent1.value.exportToCsv(filename);
            }
        };

        // Save changes back to the database
        const saveChangesToDb = async () => {
            if (!excelViewerComponent1.value || !file1Id.value || !file1Info.value) {
                saveError.value = 'Cannot save: Missing spreadsheet data or file information';
                return;
            }

            // Use the composable to save changes
            const result = await saveChangesToDbComposable(
                excelViewerComponent1.value,
                file1Id.value,
                file1Info.value,
                props.documentData.id
            );

            // Update local state based on the result
            if (result.success) {
                // Update component state with the new file information
                file1Id.value = result.newFileId || file1Id.value;
                file1Info.value = result.newFileInfo || file1Info.value;
                excelFile1Data.value = result.fileData || excelFile1Data.value;
                saveSuccess.value = result.message;
            } else {
                saveError.value = result.message;
            }
        };

        // Save changes back to the database

        // Watch for changes to documentData.id or aiDocumentId
        watch(() => [props.documentData?.id], async ([newId]) => {
            console.log('newId', newId);
            if (newId) {
                await fetchFileIds();
                fetchExcelFile(file1Id.value, excelFile1Data, file1Error, isFile1Loading, file1Info);
                if (file2Id.value) {
                    fetchExcelFile(file2Id.value, excelFile2Data, file2Error, isFile2Loading, file2Info);
                }
            }
        });

        function saveAndClose() {
            // Save logic and update document status to EMAIL_PENDING
            logger.info('Saving spreadsheet data and updating document status');

            // First, make sure any changes are saved to the database
            if (excelViewerComponent1.value && !isSaving.value) {
                saveChangesToDb().then(() => {
                    // Create an updated document object with the new status
                    const updatedDocument = {
                        ...props.documentData,
                        status: DOCUMENT_STATUS.EMAIL_PENDING,
                        file_id: file1Id.value
                    };

                    // Log the status change
                    logger.info('Document status changed', {
                        documentId: props.documentData.id,
                        previousStatus: props.documentData.status,
                        newStatus: DOCUMENT_STATUS.EMAIL_PENDING
                    });

                    // Emit save event with the updated document
                    emit('save', updatedDocument);

                    // Close the modal
                    close();
                }).catch((error) => {
                    logger.error('Error in saveAndClose', {error});
                    saveError.value = error instanceof Error ? error.message : 'An unknown error occurred';
                });
            } else {
                // Just update the status without saving changes
                const updatedDocument = {
                    ...props.documentData,
                    status: DOCUMENT_STATUS.EMAIL_PENDING
                };

                emit('save', updatedDocument);
            }
        }

        const fetchFileIds = async () => {
            // For that, we first fetch the id of the actual document
            const {data: actualDocumentData, error: documentError} = await supabase
                .from('documents')
                .select('file_id, forecast_id, report_type')
                .eq('id', props.documentData.id)
                .single();

            if (!actualDocumentData) {
                throw new Error('No document data found: ' + documentError?.message);
            }

            file1Id.value = actualDocumentData.file_id;
            reportType.value = actualDocumentData.report_type

            console.log('actualDocumentData', actualDocumentData);
            // And then we also fetch the AI generated document, now we have enough information

            try {
                const {data: aiDocument, error: aiDocumentError} = await supabase
                    .from('documents')
                    .select('file_id')
                    .eq('forecast_id', actualDocumentData.forecast_id)
                    .eq('report_type', actualDocumentData.report_type)
                    .eq('status', DOCUMENT_STATUS.AI_GENERATED)
                    .order('updated_at', {ascending: false})
                    .limit(1)
                    .single();

                if (aiDocumentError || !aiDocument) {
                    // Instead of throwing an error, set a user-friendly message
                    file2Error.value = "No AI-generated template found for comparison. Only the uploaded template will be displayed.";
                    file2Id.value = ''; // Clear the file2Id
                    return; // Exit the function early
                }

                console.log('aiDocument', aiDocument);
                file2Id.value = aiDocument.file_id;
            } catch (error) {
                // Handle any other errors that might occur
                logger.warn('Could not find AI-generated document for comparison', {error});
                file2Error.value = "No AI-generated template found for comparison. Only the uploaded template will be displayed.";
                file2Id.value = '';
            }
        }

        // When component is mounted
        onMounted(async () => {
            logger.info('SpreadsheetReviewModal mounted');
            // First, we have to fetch the ids of the two files
            try {
                await fetchFileIds();
                // Only fetch file2 if we have a valid file2Id
                fetchExcelFile(file1Id.value, excelFile1Data, file1Error, isFile1Loading, file1Info);
                if (file2Id.value) {
                    fetchExcelFile(file2Id.value, excelFile2Data, file2Error, isFile2Loading, file2Info);
                }
            } catch (error) {
                logger.error('Error in onMounted', {error});
                // If there's an error fetching file IDs, we should still try to load the first file
                if (file1Id.value) {
                    fetchExcelFile(file1Id.value, excelFile1Data, file1Error, isFile1Loading, file1Info);
                }
            }

            const cellMapping = {};

            function arrayFromTo(from: number, to: number) {
                return Array.from({length: to - from + 1}, (_, i) => i + from);
            }

            cellMapping[REPORT_TYPE.AAC] = {
                columns: ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z', 'AA', 'AB', 'AC', 'AD', 'AE', 'AF', 'AG', 'AH', 'AI'],
                rows: arrayFromTo(5, 15)
            };

            cellMapping[REPORT_TYPE.COM] = {
                columns: ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z', 'AA', 'AB', 'AC', 'AD', 'AE', 'AF', 'AG', 'AH', 'AI'],
                rows: arrayFromTo(6, 551)
            };

            cellMapping[REPORT_TYPE.LEGAL] = {
                columns: ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z', 'AA', 'AB', 'AC', 'AD', 'AE', 'AF', 'AG', 'AH'],
                rows: arrayFromTo(5, 17)
            };

            cellMapping[REPORT_TYPE.PRC] = {
                columns: ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z', 'AA', 'AB', 'AC', 'AD', 'AE', 'AF', 'AG', 'AH'],
                rows: arrayFromTo(5, 34)
            };

            // Now we also know the report type, so we can set the cell mapping

            const columns = cellMapping[reportType.value].columns;
            const rows = cellMapping[reportType.value].rows;

            // Add all combinations of columns and rows
            for (const col of columns) {
                for (const row of rows) {
                    cellDataArray.value.push({
                        fieldName: `Cell ${col}${row}`,
                        cellCoordinate: `${col}${row}`
                    });
                }
            }

            logger.info('Cell data array', {cellDataArray: cellDataArray.value});
        })

        const manualCellValues = ref<string[]>([]);
        const aiForecastValues = ref<string[]>([]);


        const handleCellValues1 = (values: string[]) => {
            manualCellValues.value = values;
        };

        const handleCellValues2 = (values: string[]) => {
            aiForecastValues.value = values;
        };

        const combinedValues = computed(() => {
            const sortedData = [...cellDataArray.value].map((item, index) => {
                const manualValue = manualCellValues.value[index] == undefined ? 'N/A' : manualCellValues.value[index];
                const aiForecastValue = aiForecastValues.value[index] == undefined ? 'N/A' : aiForecastValues.value[index];
                // Check if values are numbers
                const num1 = Number(manualValue);
                const num2 = Number(aiForecastValue);
                const isNumeric = !isNaN(num1) && !isNaN(num2);
                let relativeDeviation: number | string = 0;
                let absoluteDeviation = 0;
                let deviationStatus = "OK";
                let isOneZero = false;

                if (isNumeric) {
                    // Handle numeric comparison
                    isOneZero = num1 === 0 || num2 === 0;
                    absoluteDeviation = Math.abs(num2 - num1);

                    if (!isOneZero) {
                        relativeDeviation = Math.round(Math.abs((num2 - num1) / num1 * 100) * 10) / 10;
                        if (relativeDeviation > 5) {
                            deviationStatus = "ERROR";
                        } else if (relativeDeviation > 2) {
                            deviationStatus = "WARN";
                        }
                    } else {
                        if (absoluteDeviation > 5000) {
                            deviationStatus = "ERROR";
                        } else if (absoluteDeviation > 2000) {
                            deviationStatus = "WARN";
                        }
                    }
                } else {
                    if (!manualValue && aiForecastValue) {
                        deviationStatus = "ERROR";
                        relativeDeviation = "Text Mismatch";
                    } else if (manualValue && !aiForecastValue) {
                        deviationStatus = "ERROR";
                        relativeDeviation = "Text Mismatch";
                    } else if (manualValue && aiForecastValue && (manualValue !== aiForecastValue)) {
                        deviationStatus = "ERROR";
                        relativeDeviation = "Text Mismatch";
                    }
                }

                return {
                    ...item,
                    manualValue: manualValue,
                    aiForecastValue: aiForecastValue,
                    relativeDeviation: isNumeric
                        ? (isOneZero ? `Diff: ${absoluteDeviation}` : relativeDeviation + '%')
                        : relativeDeviation,
                    deviationStatus: deviationStatus,
                };
            }).filter(value => value.deviationStatus !== 'OK')
                .sort((a, b) => {
                // Sort by deviation status first (ERROR > WARN > OK)
                const statusOrder = {ERROR: 0, WARN: 1, OK: 2};
                const statusDiff = statusOrder[a.deviationStatus] - statusOrder[b.deviationStatus];
                if (statusDiff !== 0) return statusDiff;

                // For numeric values, sort by deviation amount
                if (typeof a.relativeDeviation === 'number' && typeof b.relativeDeviation === 'number') {
                    return b.relativeDeviation - a.relativeDeviation;
                }

                // For text mismatches, keep them at the top
                if (a.relativeDeviation === "Text Mismatch" && b.relativeDeviation !== "Text Mismatch") return -1;
                if (a.relativeDeviation !== "Text Mismatch" && b.relativeDeviation === "Text Mismatch") return 1;

                return 0;
            });
            return sortedData;
        });

        const selectedCoordinates = ref<number[] | undefined>(undefined);
        const selectedRow = ref<string | null>(null);

        const handleRowClick = (cellCoordinate: string) => {
            // Convert excel coordinate (e.g. 'A4' or 'AA4') to row/col numbers
            const col = cellCoordinate.match(/[A-Z]+/)?.[0];
            const row = parseInt(cellCoordinate.match(/\d+/)?.[0] || '0') - 1;

            if (col && row >= 0) {
                // Convert column letters to number (A=0, B=1, AA=26, etc)
                const colNum = col.split('').reduce((acc, char) =>
                    (acc * 26) + (char.charCodeAt(0) - 'A'.charCodeAt(0) + 1), 0
                ) - 1;

                selectedCoordinates.value = [row, colNum];
                selectedRow.value = cellCoordinate;
            }
        };

        return {
            excelViewerComponent1,
            excelViewerComponent2,
            isFile1Loading,
            file1Error,
            excelFile1Data,
            file1Id,
            file1Info,
            isFile2Loading,
            file2Error,
            excelFile2Data,
            file2Id,
            file2Info,
            isSaving,
            saveSuccess,
            saveError,
            close,
            saveAndClose,
            formatDate,
            formatDocumentStatus,
            retryFile1Load,
            retryFile2Load,
            exportToExcel,
            exportToCsv,
            saveChangesToDb,
            handleCellValues1,
            handleCellValues2,
            combinedValues,
            cellDataArray,
            currentLayout,
            activeViewer,
            toggleLayout,
            toggleActiveViewer,
            selectedCoordinates,
            selectedRow,
            handleRowClick,
            reportType,
        };
    }
});
</script>

<style scoped>
/* Ensure the modal has proper height constraints */
.excel-viewer-container {
    height: 100%;
    max-height: 500px;
}
</style>
